import React, { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Check, MapPin, Plus, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Pill in the dashboard header that lists every shop the current owner
 * manages and lets them switch the active one.
 *
 * Triggers `onSwitched()` after the server flips the active location so the
 * parent dashboard re-fetches scoped data (products, deals, etc.).
 */
export const LocationSwitcher = ({ activeName, onSwitched, onManage }) => {
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = () =>
    api.get("/dispensary/locations").then((r) => setLocations(r.data.locations));

  useEffect(() => {
    refresh();
  }, []);

  const switchTo = async (slug) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/dispensary/locations/${slug}/activate`);
      await refresh();
      onSwitched && (await onSwitched());
      toast.success("Switched location");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  if (!locations.length) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="dash-location-switcher"
          className="inline-flex items-center gap-2 rounded-full bg-secondary/60 hover:bg-secondary border border-border h-9 px-3 text-xs font-medium"
        >
          <MapPin className="w-3.5 h-3.5 text-accent" />
          <span className="max-w-[180px] truncate">{activeName}</span>
          {locations.length > 1 && (
            <span className="pill text-[10px] bg-muted text-muted-foreground border-border">
              {locations.length} shops
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72"
        data-testid="dash-location-menu"
      >
        <DropdownMenuLabel className="eyebrow">Your locations</DropdownMenuLabel>
        {locations.map((l) => (
          <DropdownMenuItem
            key={l.slug}
            data-testid={`dash-location-${l.slug}`}
            onSelect={(e) => {
              e.preventDefault();
              if (!l.is_active) switchTo(l.slug);
              else setOpen(false);
            }}
            className="py-2.5 cursor-pointer"
          >
            <div className="flex items-start gap-2.5 w-full">
              <div className="w-4 mt-0.5 shrink-0">
                {busy && !l.is_active ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : l.is_active ? (
                  <Check className="w-4 h-4 text-emerald-400" />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{l.name}</div>
                <div className="text-xs text-muted-foreground">
                  {l.city || "—"} · {l.products_count} products
                </div>
              </div>
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="dash-location-manage"
          onSelect={() => {
            onManage && onManage();
            setOpen(false);
          }}
          className="cursor-pointer"
        >
          <Plus className="w-3.5 h-3.5 mr-2" /> Add or manage locations
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
